import { cn } from "@/lib/utils";

type Props<T extends string> = {
  currentTab: T;
  onTabClicked: (tab: T) => void;
  options: {
    label: string;
    value: T;
    showDot?: boolean;
    isNew?: boolean;
  }[];
  renderCurrentTab: (currentTab: T) => React.ReactNode;
  classNameOptions?: string;
  animateContent?: boolean;
};

export default function Tabs<T extends string>({
  options,
  renderCurrentTab,
  currentTab,
  onTabClicked,
  classNameOptions,
  animateContent = true,
}: Props<T>) {
  return (
    <div className="flex h-full w-full flex-col overflow-hidden">
      <div
        className={cn(
          "relative mx-6 flex flex-row gap-6 self-stretch pt-6",
          classNameOptions,
        )}
      >
        <div className="absolute right-0 bottom-0 left-0 h-px rounded-full bg-border" />
        {options.map((option) => (
          <TabOption
            key={option.value}
            {...option}
            isActive={option.value === currentTab}
            onClick={onTabClicked}
          />
        ))}
      </div>
      <div
        key={currentTab}
        className={cn(
          "flex-1 self-stretch overflow-hidden",
          animateContent && "animate-fade-in",
        )}
      >
        {renderCurrentTab(currentTab)}
      </div>
    </div>
  );
}

type TabOptionProps<T extends string> = {
  label: string;
  value: T;
  showDot?: boolean;
  isActive: boolean;
  isNew?: boolean;
  onClick: (value: T) => void;
};

function TabOption<T extends string>({
  label,
  value,
  showDot,
  isActive,
  isNew,
  onClick,
}: TabOptionProps<T>) {
  return (
    <div className="flex flex-row items-start">
      <button
        className={cn(
          "relative flex h-8 cursor-pointer flex-col text-sm font-medium",
          isActive ? "text-text-primary" : "text-text-secondary",
        )}
        onClick={() => onClick(value)}
      >
        <div className="flex items-center gap-2">
          {label}
          {showDot && (
            <span
              aria-hidden="true"
              className="h-2 w-2 shrink-0 rounded-full border border-white bg-linear-to-br from-primary-400 to-secondary-400"
            />
          )}
        </div>
        {isActive && (
          <div className="absolute right-0 bottom-0 left-0 h-[1.5px] rounded-full bg-text-primary" />
        )}
      </button>
      {isNew && (
        <div className="mt-0.5 ml-2.5 flex items-center rounded-full bg-linear-to-br from-primary-100 to-secondary-100 px-1.5 py-px">
          <span className="bg-linear-to-r from-primary-400 to-secondary-400 bg-clip-text text-[10px] font-bold text-transparent">
            NEW
          </span>
        </div>
      )}
    </div>
  );
}
